import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Crown, Medal, EyeOff, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";

function rankIcon(rank) {
  if (rank === 1) return <Crown className="w-5 h-5 text-lemon" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-pebble" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-watermelon" />;
  return <span className="font-heading font-bold text-stone">#{rank}</span>;
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/leaderboard/weekly");
      setData(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleOptIn = async () => {
    if (!data) return;
    setToggling(true);
    try {
      const next = !data.my_opt_in;
      await api.post("/student/leaderboard-opt-in", { opt_in: next });
      toast.success(next ? "You're now on the leaderboard" : "You've opted out of the leaderboard");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setToggling(false); }
  };

  return (
    <AppLayout variant="student">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lemon/40 text-ink font-bold text-sm mb-2">
              <Trophy className="w-4 h-4" /> Weekly Leaderboard
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Top MathWhizzes</h1>
            <p className="text-stone mt-1">Last 7 days · ranked by points (10 per correct + 5 per attempt).</p>
          </div>
          <Link to="/student" data-testid="leaderboard-back-link" className="text-sm font-bold text-stone hover:text-ink">← Back</Link>
        </div>

        {user?.role === "student" && data && (
          <div className="mb-6 p-4 bg-white rounded-3xl card-tactile flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-pebble">Your rank this week</div>
              <div data-testid="my-rank" className="font-heading text-3xl font-bold text-ink">
                {data.my_opt_in ? (data.my_rank ? `#${data.my_rank}` : "—") : "Opted out"}
              </div>
            </div>
            <button
              data-testid="toggle-optin-button"
              onClick={toggleOptIn}
              disabled={toggling}
              className={`inline-flex items-center gap-2 h-11 px-4 rounded-full font-bold text-sm btn-tactile ${
                data.my_opt_in ? "bg-white border-2 border-cloud text-ink" : "bg-sky text-white"
              }`}
            >
              {data.my_opt_in ? <><EyeOff className="w-4 h-4" /> Hide me</> : <><Eye className="w-4 h-4" /> Join leaderboard</>}
            </button>
          </div>
        )}

        <div className="bg-white rounded-3xl card-tactile overflow-hidden">
          {loading ? (
            <div className="p-8 text-stone">Loading…</div>
          ) : (data?.top ?? []).length === 0 ? (
            <div data-testid="leaderboard-empty" className="p-10 text-center text-stone">
              No results yet this week. Be the first to practice!
            </div>
          ) : (
            <ol data-testid="leaderboard-list" className="divide-y divide-cloud">
              {data.top.map((row) => (
                <li key={row.student_id} data-testid={`leaderboard-row-${row.rank}`} className={`flex items-center gap-4 p-4 sm:p-5 ${row.student_id === user?.id ? "bg-lemon/20" : ""}`}>
                  <div className="w-10 flex items-center justify-center">{rankIcon(row.rank)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-bold text-ink truncate">
                      {row.name}{row.student_id === user?.id && <span className="ml-2 text-xs font-bold text-sky">YOU</span>}
                    </div>
                    <div className="text-xs text-stone">{row.attempts} attempt{row.attempts === 1 ? "" : "s"} · {row.avg_accuracy}% avg</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-2xl font-bold text-watermelon tabular-nums">{row.points}</div>
                    <div className="text-xs font-bold uppercase tracking-wider text-pebble">pts</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
